import {
  Box,
  FlexBox,
  Icon,
  IconButton,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import snakeCase from 'lodash/snakeCase';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSetState, useUpdateEffect } from 'react-use';
import { v4 as uuidv4 } from 'uuid';

import { CustomFieldSelectAttributeOption } from 'apis/customFields';
import { HookFormSwitch, HookFormText } from 'components/hookForm';
import { getObjectByPath } from 'utils/getObjectByPath';
import { useAppModal } from 'utils/modal';
import { convertTreeToFlatItems, treeFind } from 'utils/tree';
import {
  useOrderedDragDrop,
  UseOrderedDragDropOptions,
} from 'utils/useOrderedDragDrop';

import { optionOrderIndexStep } from '../../optionOrderIndexStep';
import {
  OptionInputField,
  OptionUserInputEvent,
} from '../../optionsFormField.types';
import { optionsKey } from '../../optionsKey';
import { Options } from '../options/options';

import styles from './option.module.scss';

type OptionProps = {
  /** The name of the parent. */
  parentName: string;
  /** The index of the field. */
  fieldIndex: number;
  /** The order index to be used in drag and drop. */
  orderIndex: number;
  /**
   * Whether it is the last option in the list of all options including the
   * blank one.
   */
  isLastOption: boolean;
  /** Whether the value has been changed by the user. */
  isValueChangedByUser?: boolean;
  /** Whether the parent of the option is deactivated. */
  isParentDeactivated?: boolean;
  /** The callback for when the option is removed from the list. */
  onRemove: (index: number) => void;
  /** The callback for when a user directly changes a field value. */
  onUserInput: (event: OptionUserInputEvent) => void;
} & Pick<UseOrderedDragDropOptions, 'onMove'>;

/** Represents an option in the options form field for custom select attribute. */
export const Option = memo(function Option({
  fieldIndex,
  isLastOption,
  isParentDeactivated,
  isValueChangedByUser,
  orderIndex,
  parentName,
  onMove,
  onRemove,
  onUserInput,
}: OptionProps): JSX.Element {
  const { modal } = useAppModal('settings:customAttribute');
  const rowRef = useRef<HTMLDivElement>(null);
  const optionRef = useRef<HTMLDivElement>(null);
  const rowHandleRef = useRef<HTMLDivElement>(null);
  // TODO: [TEAMS-13693] add `AttributeFormFieldValuesHavingOptions` generic back after updating to RHF v8
  const { control, formState, setValue, trigger, getValues } = useFormContext();
  const { errors, dirtyFields } = formState;
  const optionFormName = parentName
    ? (`${parentName}.${optionsKey}.${fieldIndex}` as const)
    : (`${optionsKey}.${fieldIndex}` as const);
  const idFormName = `${optionFormName}.id` as const;
  const labelFormName = `${optionFormName}.label` as const;
  const valueFormName = `${optionFormName}.value` as const;
  const optionsFormName = `${optionFormName}.${optionsKey}` as const;
  const deactivatedFormName = `${optionFormName}.is_deactivated` as const;

  const { fields, append, remove } = useFieldArray({
    control,
    name: optionsFormName,
  });

  const optionId = useMemo(
    () => getValues(idFormName),
    [getValues, idFormName],
  );

  const [{ label: optionLabel, value: optionValue }, setOptionValues] =
    useSetState<Pick<CustomFieldSelectAttributeOption, 'label' | 'value'>>({
      label: getValues(labelFormName) ?? '',
      value: getValues(valueFormName) ?? '',
    });

  const hasOptions = !!fields.length;
  const [isOptionsExpanded, setIsOptionsExpanded] = useState(false);

  const optionFromEndpoint = useMemo(() => {
    if (modal?.arg.mode === 'edit' && modal.arg.attribute.type === 'select') {
      return treeFind(
        modal.arg.attribute.options,
        ({ id }) => optionId === id,
        'options',
      );
    }
  }, [modal, optionId]);

  const hasOptionsFromEndpoint = !!optionFromEndpoint?.options?.length;
  const optionErrors = getObjectByPath(errors, optionFormName);
  const optionDirtyFields = getObjectByPath(dirtyFields, optionFormName);
  const isOptionFromEndpoint = !!optionFromEndpoint;
  const isOptionBlank = !optionLabel && !optionValue;
  const isOptionValid = !optionErrors?.label && !optionErrors?.value;
  const isOptionsValid = !optionErrors?.options;
  const isOptionDeactivated = useWatch({ control, name: deactivatedFormName });
  const isInputRequired = hasOptions || !isOptionBlank;
  const showAlert = isInputRequired && !isLastOption && !isOptionValid;

  const { isDragging, isOver } = useOrderedDragDrop({
    type: parentName || 'options',
    index: orderIndex,
    source: rowHandleRef,
    target: rowRef,
    preview: optionRef,
    onMove,
    triggerSwap: 'drop',
  });

  /** Handles text input. */
  const handleInput = useCallback(
    (field: OptionInputField, event) => {
      setOptionValues({ [field]: event.target.value });

      if (!isOptionFromEndpoint && field === 'label' && !isValueChangedByUser) {
        const label = event.target.value;

        setValue(valueFormName, snakeCase(label), {
          shouldTouch: true,
          shouldDirty: true,
        });
      }

      if (optionErrors?.value) {
        trigger('options');
      }

      onUserInput({ index: fieldIndex, field });
    },
    [
      fieldIndex,
      isOptionFromEndpoint,
      isValueChangedByUser,
      optionErrors,
      valueFormName,
      onUserInput,
      setOptionValues,
      setValue,
      trigger,
    ],
  );

  /** Handles input blur. */
  const handleBlur = useCallback(
    (field: OptionInputField, event) => {
      if (field === 'value' && optionDirtyFields?.value === true) {
        trigger('options');
      }
    },
    [trigger, optionDirtyFields],
  );

  /** Handles input focus. */
  const handleFocus = useCallback(
    (field: OptionInputField, event) => {
      if (field === 'value' && !isValueChangedByUser) {
        trigger('options');
      }
    },
    [trigger, isValueChangedByUser],
  );

  /** Handles creating nested options under the current option. */
  const createTree = useCallback(() => {
    setIsOptionsExpanded(true);

    if (isOptionBlank) {
      // We do this so we can make the field touched so that we can show the warning
      setValue(optionFormName, getValues(optionFormName), {
        shouldTouch: true,
      });
    }

    append(
      {
        id: uuidv4(),
        label: '',
        value: '',
        is_deactivated: isOptionDeactivated,
        order_index: optionOrderIndexStep,
        parent_id: optionId,
      },
      { shouldFocus: false },
    );
  }, [
    isOptionBlank,
    isOptionDeactivated,
    optionFormName,
    optionId,
    append,
    getValues,
    setValue,
  ]);

  // Propagate active state change to children
  useUpdateEffect(() => {
    const options = getValues(optionsFormName);
    const toggledOptions = toggleActive(options, isOptionDeactivated);
    setValue(optionsFormName, toggledOptions);
  }, [isOptionDeactivated, optionsFormName, getValues, setValue]);

  /** Handles removing nested options from the current option. */
  const removeTree = useCallback(() => {
    setIsOptionsExpanded(false);

    remove();
  }, [remove]);

  // Handles validating the option when options are removed.
  useUpdateEffect(() => {
    if (!hasOptions) {
      trigger(optionFormName);
    }
  }, [hasOptions]);

  // Handles deactivating the parent when all its children are deactivated.
  useUpdateEffect(() => {
    if (parentName && isOptionDeactivated) {
      const siblings = getValues(`${parentName}.${optionsKey}` as const);
      const isAllSiblingsDeactivated = siblings
        .filter(({ label, value }) => !!label && !!value)
        .every(({ is_deactivated }) => is_deactivated);

      if (isAllSiblingsDeactivated) {
        setValue(`${parentName}.is_deactivated` as const, true, {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }
  }, [isOptionDeactivated, parentName, optionFormName, getValues, setValue]);

  return (
    <div
      ref={rowRef}
      className={classnames(styles.root, {
        [styles.hasParent]: !!parentName,
        [styles.isDragging]: isDragging,
        [styles.isOver]: isOver && !isLastOption,
      })}
    >
      <div
        ref={optionRef}
        className={classnames(styles.option, {
          [styles.nestedError]: !isOptionsValid && !isOptionsExpanded,
        })}
      >
        <FlexBox className={styles.flexBox} justifyContent="space-between">
          <FlexBox>
            {hasOptions && (
              <IconButton
                variant="tertiary"
                icon={isOptionsExpanded ? 'angle-down' : 'angle-right'}
                onClick={() => setIsOptionsExpanded(!isOptionsExpanded)}
              />
            )}
          </FlexBox>

          <FlexBox>
            {!isLastOption && (
              <div ref={rowHandleRef} className={styles.handle}>
                <Icon name="drag" />
              </div>
            )}
          </FlexBox>
        </FlexBox>

        <HookFormText
          control={control}
          name={labelFormName}
          noAlert={!showAlert}
          noLabel
          rules={{
            required: {
              value: isInputRequired,
              message: 'The label is required',
            },
          }}
          componentProps={{
            placeholder: `Option ${fieldIndex + 1}`,
            onBlur: (event) => handleBlur('label', event),
            onFocus: (event) => handleFocus('value', event),
            onInput: (event) => handleInput('label', event),
          }}
        />

        <HookFormText
          control={control}
          name={valueFormName}
          noAlert={!showAlert}
          noLabel
          rules={
            !isOptionFromEndpoint
              ? {
                  required: {
                    value: isInputRequired,
                    message: 'The value is required',
                  },
                  validate: {
                    conflictingValue: (value) => {
                      const conflictingOptions = convertTreeToFlatItems(
                        getValues('options') ?? [],
                        {
                          childrenKey: 'options',
                        },
                      ).filter(
                        (flatOption) =>
                          flatOption.id !== optionId &&
                          flatOption.value &&
                          flatOption.value === value,
                      );

                      return (
                        conflictingOptions.length === 0 ||
                        `Value is already in use`
                      );
                    },
                  },
                }
              : undefined
          }
          componentProps={{
            placeholder: `option_${fieldIndex + 1}`,
            readOnly: isOptionFromEndpoint,
            onBlur: (event) => handleBlur('value', event),
            onFocus: (event) => handleFocus('value', event),
            onInput: (event) => handleInput('value', event),
          }}
        />

        <FlexBox className={styles.flexBox}>
          {!isLastOption && (
            <Tooltip
              dark
              content={
                isOptionFromEndpoint
                  ? 'Previously created option can not be removed'
                  : hasOptions
                  ? 'Remove option and all nested options'
                  : 'Remove option'
              }
            >
              <IconButton
                icon="minus"
                variant="secondary"
                disabled={isOptionFromEndpoint}
                onClick={() => onRemove(fieldIndex)}
              />
            </Tooltip>
          )}
        </FlexBox>

        <FlexBox className={styles.flexBox}>
          {!isLastOption && (
            <>
              {hasOptions ? (
                <Tooltip
                  dark
                  content={
                    hasOptionsFromEndpoint
                      ? 'Previously created nested options can not be removed'
                      : 'Remove all nested options'
                  }
                >
                  <IconButton
                    icon="close"
                    variant="secondary"
                    disabled={hasOptionsFromEndpoint}
                    onClick={() => removeTree()}
                  />
                </Tooltip>
              ) : (
                <Tooltip dark content="Add nested options">
                  <IconButton
                    icon="dropdown-list"
                    variant="secondary"
                    onClick={() => createTree()}
                  />
                </Tooltip>
              )}
            </>
          )}
        </FlexBox>

        <FlexBox className={styles.flexBox}>
          {!isLastOption && (
            <HookFormSwitch
              control={control}
              name={deactivatedFormName}
              transformValue={(value) => !value}
              componentProps={{
                disabled: isParentDeactivated,
                onChange: () => trigger(optionsKey),
              }}
            />
          )}
        </FlexBox>
      </div>

      {hasOptions && isOptionsExpanded && (
        <Box className={styles.options}>
          <Options parentName={optionFormName} parentId={optionId} />
        </Box>
      )}
    </div>
  );
});

/** Returns recursively toggled active state of the option and its children. */
function toggleActive(
  options: readonly CustomFieldSelectAttributeOption[],
  deactivate: boolean,
): readonly CustomFieldSelectAttributeOption[] {
  return options.map((option) => ({
    ...option,
    is_deactivated: deactivate,
    ...(option.options
      ? { options: toggleActive(option.options, deactivate) }
      : {}),
  }));
}
